import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';

import type {
  EventPlace,
  PlaceSuggestion,
  PlaceView,
  TravelAutoStatus,
  TravelMode,
} from '@amumin/api-client';
import { PLACE_DEBOUNCE_MS, PLACE_QUERY_MIN } from '@amumin/domain/maps';
import { t } from '@amumin/i18n';
import { cx, Icon, Pictogram } from '@amumin/ui-core';

import { api, isApiError } from '../api.js';
import { moduleOn } from '../state/modules.js';
import { appText, ownMemberId } from '../state/session.js';
import styles from './PlaceField.module.css';

// "Waar" met plaatsen zoeken (0034, golf 3; oprichter #6 "Google Maps bedrijven zoeken bij Waar";
// docs/DESIGN.md §8.KA.1). Eén veld: typen blijft gewone tekst; vanaf 3 letters en 300 ms stilte
// vraagt de webapp ONZE server om voorstellen (de server vraagt Google; de browser nooit). Kies je
// een voorstel, dan staat de plek (naam en adres) eronder en rekent het opslaan de reistijd uit.
// Eigen blok met eigen staat (module Plaatsen zoeken), zoals de vaste activiteit: de editor vraagt
// bij opslaan `commit(seriesId)`; zonder wijziging gebeurt er niets.
// fix/afspraakformulier (oprichter 7 okt 2026): een schakelaar "Zoeken via Google Maps" naast het
// label. Uit = gewoon tekst, geen enkel verzoek naar de server voor voorstellen; aan = voorstellen.
// De keuze blijft op dit apparaat, per gebruiker (localStorage). Module uit of geen sleutel op de
// server: de schakelaar staat grijs en de tekst zegt waarom.

export interface PlaceFieldHandle {
  /** Bewaart de gekozen plek (of haalt hem weg) na het opslaan van de afspraak. */
  commit: (seriesId: string) => Promise<void>;
  dirty: () => boolean;
}

const MODES: TravelMode[] = ['drive', 'bicycle', 'walk'];
const MODE_PICTO: Readonly<Record<TravelMode, string>> = {
  drive: 'car',
  bicycle: 'cycling',
  walk: 'walk',
};

/** Willekeurige zoeksessie (Google: URL-veilig, hooguit 36 tekens). */
function newSession(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

type Problem = 'not_configured' | 'unavailable' | 'too_many' | null;

function googleKey(): string {
  return `amumin.placeGoogle.${ownMemberId.value ?? 'me'}`;
}

/** Staat zoeken via Google aan voor deze gebruiker op dit apparaat? Standaard aan. */
export function placeGoogleOn(): boolean {
  try {
    return localStorage.getItem(googleKey()) !== '0';
  } catch {
    return true;
  }
}

function savePlaceGoogle(on: boolean): void {
  try {
    localStorage.setItem(googleKey(), on ? '1' : '0');
  } catch {
    // Privévenster of geblokkeerde opslag: dan alleen voor nu.
  }
}

export function PlaceField({
  id,
  label,
  value,
  onInput,
  seriesId,
  handleRef,
  onAuto,
  available = true,
}: {
  id: string;
  label: string;
  /** De tekst bij "Waar" (event.location). */
  value: string;
  onInput: (v: string) => void;
  /** Bestaande afspraak (series_id); null = nieuw. */
  seriesId: string | null;
  handleRef: { current: PlaceFieldHandle | null };
  /**
   * Is er een plek gekozen, en wat is de berekende reistijd (null = nog niet bekend)? De editor
   * toont daarmee "Automatisch (30 min)" naast "Geen" (review g3-fs r1).
   */
  onAuto?: (x: { chosen: boolean; minutes: number | null }) => void;
  /** Module Plaatsen zoeken aan? Uit: gewoon tekst, de schakelaar grijs met de reden. */
  available?: boolean;
}) {
  const a = appText.value;
  const x = a.places;
  const travelOn = moduleOn('travel_time');
  const initial = useSignal<EventPlace | null>(null);
  const chosen = useSignal<PlaceView | null>(null);
  const mode = useSignal<TravelMode | null>(null);
  const items = useSignal<PlaceSuggestion[]>([]);
  const open = useSignal(false);
  const active = useSignal(-1);
  const searching = useSignal(false);
  const problem = useSignal<Problem>(null);
  const retryMinutes = useSignal(1);
  const choosing = useSignal(false);
  // Zoeksessie: één per invulbeurt (de server geeft hem door aan Google).
  const session = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);
  const off = useRef(false);
  const google = useSignal(placeGoogleOn());
  const notConfigured = useSignal(false);
  const usable = available && !notConfigured.value;
  const searchOn = usable && google.value;

  useEffect(() => {
    let alive = true;
    initial.value = null;
    chosen.value = null;
    mode.value = null;
    if (!seriesId || !available) return;
    void api.maps.eventPlace
      .get(seriesId)
      .then((res) => {
        if (!alive) return;
        initial.value = res;
        chosen.value = res.place;
        mode.value = res.travel_mode;
      })
      .catch(() => {
        // Geen plek of module uit: gewoon een tekstveld.
      });
    return () => {
      alive = false;
    };
  }, [seriesId]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const isDirty = (): boolean => {
    const i = initial.value;
    const before = i?.place?.place_id ?? null;
    const now = chosen.value?.place_id ?? null;
    if (before !== now) return true;
    if (!now) return false;
    if ((i?.travel_mode ?? null) !== mode.value) return true;
    // Vorige keer lukte de reistijd niet (Google even weg): bij opslaan opnieuw proberen.
    return i?.auto_status === 'unavailable';
  };

  handleRef.current = {
    dirty: isDirty,
    commit: async (target: string) => {
      if (!isDirty()) return;
      const place = chosen.value;
      const version = initial.value?.version ?? 0;
      const res = await api.maps.eventPlace.set(target, {
        place_id: place ? place.place_id : null,
        ...(place && session.current ? { session_token: session.current } : {}),
        ...(place ? { travel_mode: mode.value } : {}),
        if_version: version,
      });
      initial.value = res.place;
      session.current = null;
    },
  };

  const search = (text: string, on: boolean = searchOn) => {
    if (timer.current) clearTimeout(timer.current);
    const q = text.trim();
    if (!on || off.current || q.length < PLACE_QUERY_MIN) {
      items.value = [];
      open.value = false;
      searching.value = false;
      return;
    }
    searching.value = true;
    timer.current = setTimeout(() => {
      const mine = (seq.current += 1);
      if (!session.current) session.current = newSession();
      api.maps.places
        .autocomplete(q, session.current)
        .then((res) => {
          if (mine !== seq.current) return;
          problem.value = null;
          items.value = res.items;
          active.value = res.items.length > 0 ? 0 : -1;
          open.value = true;
        })
        .catch((e: unknown) => {
          if (mine !== seq.current) return;
          items.value = [];
          open.value = false;
          if (isApiError(e, 503, 'maps_not_configured') || isApiError(e, 404)) {
            // Niet ingesteld (of de module ging net uit): voor deze invulbeurt niet meer vragen.
            off.current = true;
            notConfigured.value = true;
            problem.value = isApiError(e, 404) ? null : 'not_configured';
          } else if (isApiError(e, 429)) {
            const d = (e as { details?: { retry_after_s?: number } }).details;
            retryMinutes.value = Math.max(1, Math.ceil((d?.retry_after_s ?? 60) / 60));
            problem.value = 'too_many';
          } else problem.value = 'unavailable';
        })
        .finally(() => {
          if (mine === seq.current) searching.value = false;
        });
    }, PLACE_DEBOUNCE_MS);
  };

  const type = (text: string) => {
    onInput(text);
    // Zelf verder typen na een keuze: dan is het weer gewone tekst (de plek vervalt bij opslaan).
    if (chosen.value && text.trim() !== chosen.value.name) chosen.value = null;
    search(text);
  };

  const toggle = () => {
    if (!usable) return;
    const next = !google.value;
    google.value = next;
    savePlaceGoogle(next);
    problem.value = null;
    if (next) {
      search(value, true);
      return;
    }
    // Uit: lopende zoekvraag negeren, geen voorstellen meer.
    if (timer.current) clearTimeout(timer.current);
    seq.current += 1;
    items.value = [];
    open.value = false;
    searching.value = false;
  };

  const choose = async (s: PlaceSuggestion) => {
    open.value = false;
    items.value = [];
    choosing.value = true;
    seq.current += 1;
    try {
      const place = await api.maps.places.details(s.place_id, session.current ?? undefined);
      // De keuze sluit de zoeksessie af (Google); opnieuw zoeken start een nieuwe.
      session.current = null;
      chosen.value = place;
      onInput(place.name.slice(0, 120));
      problem.value = null;
    } catch (e) {
      if (isApiError(e, 429)) {
        const d = (e as { details?: { retry_after_s?: number } }).details;
        retryMinutes.value = Math.max(1, Math.ceil((d?.retry_after_s ?? 60) / 60));
        problem.value = 'too_many';
      } else
        problem.value = isApiError(e, 503, 'maps_not_configured')
          ? 'not_configured'
          : 'unavailable';
      onInput(s.name.slice(0, 120));
    } finally {
      choosing.value = false;
    }
  };

  const onKey = (e: KeyboardEvent) => {
    const list = items.value;
    if (e.key === 'Escape') {
      if (open.value) {
        e.stopPropagation();
        open.value = false;
      }
      return;
    }
    if (!open.value || list.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      active.value = (active.value + 1) % list.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      active.value = (active.value - 1 + list.length) % list.length;
    } else if (e.key === 'Enter') {
      const pick = list[active.value];
      if (pick) {
        e.preventDefault();
        void choose(pick);
      }
    }
  };

  const listId = `${id}-list`;
  const place = chosen.value;
  const init = initial.value;
  const samePlace = place && init?.place?.place_id === place.place_id;
  const effectiveMode = mode.value ?? init?.default_mode ?? 'drive';
  const autoFresh =
    samePlace && (init?.travel_mode ?? null) === mode.value ? init?.auto_status : null;
  const statusText = (s: TravelAutoStatus | null | undefined): string | null => {
    if (!s) return x.travelPending;
    if (s === 'ok' && init?.auto_minutes) {
      return t(x.travelAuto, { minutes: init.auto_minutes, mode: x.modeWith[effectiveMode] });
    }
    return s === 'ok' ? x.travelPending : x.travelStatus[s];
  };
  const autoMinutes = autoFresh === 'ok' && init?.auto_minutes ? init.auto_minutes : null;
  const hasPlace = !!place;
  useEffect(() => {
    onAuto?.({ chosen: hasPlace, minutes: hasPlace ? autoMinutes : null });
  }, [hasPlace, autoMinutes]);

  return (
    <div class={styles.field} data-name="place-field">
      <div class={styles.head}>
        <label class={styles.label} for={id}>
          {label}
        </label>
        <button
          type="button"
          role="switch"
          class={styles.switch}
          aria-checked={searchOn ? 'true' : 'false'}
          aria-describedby={`${id}-hint`}
          disabled={!usable}
          onClick={toggle}
          data-name="place-google"
        >
          <span>{x.google}</span>
          <span class={styles.track} aria-hidden="true">
            <span class={styles.knob} />
          </span>
        </button>
      </div>
      <div class={styles.box}>
        <span class={styles.lead} aria-hidden="true">
          <Icon id={searching.value || choosing.value ? 'loader-circle' : 'map-pin'} size={18} />
        </span>
        <input
          id={id}
          name={id}
          class={styles.input}
          type="text"
          value={value}
          maxLength={120}
          autoComplete="off"
          placeholder={searchOn ? x.placeholder : a.editor.locationPlaceholder}
          role={searchOn ? 'combobox' : undefined}
          aria-autocomplete={searchOn ? 'list' : undefined}
          {...(searchOn
            ? { 'aria-expanded': open.value ? 'true' : 'false', 'aria-controls': listId }
            : {})}
          aria-describedby={`${id}-hint`}
          {...(open.value && active.value >= 0
            ? { 'aria-activedescendant': `${listId}-${active.value}` }
            : {})}
          onInput={(e) => type((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={onKey}
          onBlur={() => {
            setTimeout(() => (open.value = false), 150);
          }}
          onFocus={() => {
            if (items.value.length > 0) open.value = true;
          }}
        />
      </div>
      {open.value ? (
        <div class={styles.popover}>
          {items.value.length === 0 ? (
            <p class={styles.none} role="status">
              {x.noResults}
            </p>
          ) : (
            <ul id={listId} class={styles.list} role="listbox" aria-label={x.suggestions}>
              {items.value.map((s, i) => (
                <li
                  key={s.place_id}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active.value ? 'true' : 'false'}
                  class={cx(styles.option, i === active.value && styles.optionActive)}
                  data-name="place-suggestion"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void choose(s)}
                >
                  <span class={styles.optionIcon} aria-hidden="true">
                    <Icon id="map-pin" size={16} />
                  </span>
                  <span class={styles.optionText}>
                    <span class={styles.optionName}>{s.name}</span>
                    {s.secondary ? <span class={styles.optionSub}>{s.secondary}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p class={styles.attribution}>{x.attribution}</p>
        </div>
      ) : null}
      {/* Onder de open voorstellen geen hulptekst (die zou half achter de lijst staan). */}
      <p id={`${id}-hint`} class={styles.hint} role="status" hidden={open.value}>
        {problem.value === 'not_configured'
          ? x.notConfigured
          : problem.value === 'unavailable'
            ? x.unavailable
            : problem.value === 'too_many'
              ? t(x.tooMany, { minutes: retryMinutes.value })
              : !available
                ? x.moduleOff
                : place
                  ? ''
                  : searchOn
                    ? x.hintSearch
                    : x.googleOff}
      </p>

      {place ? (
        <div class={styles.chosen} data-name="place-chosen">
          <span class={styles.chosenIcon} aria-hidden="true">
            <Icon id="map-pin" size={20} />
          </span>
          <span class={styles.chosenText}>
            <span class={styles.chosenLabel}>{x.chosen}</span>
            <span class={styles.chosenName}>{place.name}</span>
            {place.address ? <span class={styles.chosenSub}>{place.address}</span> : null}
          </span>
          <button
            type="button"
            class={styles.linkButton}
            data-name="place-remove"
            onClick={() => {
              chosen.value = null;
            }}
          >
            {x.change}
          </button>
        </div>
      ) : null}

      {place && travelOn ? (
        <div class={styles.travel} data-name="place-travel">
          <p class={styles.travelTitle} id={`${id}-mode`}>
            {x.modeTitle}
          </p>
          <div class={styles.modes} role="radiogroup" aria-labelledby={`${id}-mode`}>
            <button
              type="button"
              role="radio"
              aria-checked={mode.value === null ? 'true' : 'false'}
              class={cx(styles.mode, mode.value === null && styles.modeOn)}
              data-name="place-mode-usual"
              onClick={() => (mode.value = null)}
            >
              <Pictogram
                icon={MODE_PICTO[init?.default_mode ?? 'drive']}
                label={x.modes[init?.default_mode ?? 'drive']}
                size={28}
                decorative
              />
              <span>
                {x.modeUsual}
                <span class={styles.modeSub}>{x.modes[init?.default_mode ?? 'drive']}</span>
              </span>
            </button>
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode.value === m ? 'true' : 'false'}
                class={cx(styles.mode, mode.value === m && styles.modeOn)}
                data-name={`place-mode-${m}`}
                onClick={() => (mode.value = m)}
              >
                <Pictogram icon={MODE_PICTO[m]} label={x.modes[m]} size={28} decorative />
                <span>{x.modes[m]}</span>
              </button>
            ))}
          </div>
          <p class={styles.travelLine} data-name="place-travel-line">
            <Icon id="clock" size={16} />
            <span>{statusText(autoFresh)}</span>
          </p>
          {autoFresh === 'ok' ? <p class={styles.hintSmall}>{x.travelAutoHint}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
